import { useEffect, useState } from 'react'
import { Brand } from '../components/Brand'
import { actOnInvitation, inspectInvitation } from '../features/sharing/api'
import type { ProjectionLevel } from '../features/sharing/contracts'
import { useFragmentToken } from '../offline'

type Invitation = { inviterDisplayName: string; projectionLevel: ProjectionLevel; expiresAt: string }
type State = 'loading' | 'pending' | 'accepted' | 'declined' | 'error'

export function InviteScreen() {
  const token = useFragmentToken()
  const [state, setState] = useState<State>(token ? 'loading' : 'error')
  const [invitation, setInvitation] = useState<Invitation | null>(null)

  useEffect(() => {
    if (!token) return
    let active = true
    void inspectInvitation(token).then((value) => {
      if (!active) return
      setInvitation(value)
      setState('pending')
    }).catch(() => { if (active) setState('error') })
    return () => { active = false }
  }, [token])

  const act = async (action: 'accept' | 'decline') => {
    if (!token) return
    setState('loading')
    try {
      const result = await actOnInvitation(token, action)
      setState(result.state)
    } catch {
      setState('error')
    }
  }

  const title = state === 'accepted' ? 'Invitation accepted'
    : state === 'declined' ? 'Invitation declined'
    : state === 'pending' && invitation ? `${invitation.inviterDisplayName} invited you`
    : state === 'loading' ? 'Checking invitation…'
    : 'Invitation unavailable'

  return <main className="centred-page"><section className="auth-card panel"><Brand /><span className="overline">Private invitation</span>
    <h1>{title}</h1>
    {state === 'loading' && <p role="status">The single-use invitation is being checked securely.</p>}
    {state === 'error' && <p role="alert">This invitation is invalid, expired, revoked, already used, or sharing is not configured.</p>}
    {state === 'pending' && invitation && <><p>You can see {invitation.inviterDisplayName}’s {invitation.projectionLevel} projection until {new Date(invitation.expiresAt).toLocaleDateString()}.</p><div className="callout">Accepting does not share anything of yours.</div><div className="button-row"><button type="button" className="button button-primary" onClick={() => void act('accept')}>Accept</button><button type="button" className="button" onClick={() => void act('decline')}>Decline</button></div></>}
    {state === 'accepted' && <><p>The person now appears on your private Scoreboard at the level they granted.</p><a className="button button-primary" href="/?screen=scoreboard">Open Scoreboard</a></>}
    {state === 'declined' && <p>The invitation was declined. The inviter was not notified.</p>}
  </section></main>
}
